import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getApplicationSettings } from 'src/modules/applications/slices';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { useCampaignConfigService } from '../services';
import {
  setLoading,
  setActiveCategories,
  setConfigs,
  setLastFetchedCompanyId,
  getCampaignConfigs as getCampaignConfigsFromStore,
  getLastFetchedCompanyId,
} from '../slices';

export const useCampaignConfigLoader = () => {
  const dispatch = useDispatch();
  const { getCampaignConfigs, getActiveCampaignConfigCategories } = useCampaignConfigService();
  const applicationSettings = useSelector(getApplicationSettings);
  const currentCompanyId = useSelector(getCurrentCompanyId);
  const lastFetchedCompanyId = useSelector(getLastFetchedCompanyId);
  const campaignConfigs = useSelector(getCampaignConfigsFromStore);

  const loadCampaignConfigs = useCallback(async (
    forceRefresh = false,
    companyIdOverride?: string,
    applicationIdOverride?: string,
  ) => {
    const companyIdToUse = companyIdOverride || currentCompanyId;
    const applicationIdToUse = applicationIdOverride ?? applicationSettings?.applicationId;

    if (!companyIdToUse) {
      return;
    }

    if (!forceRefresh && lastFetchedCompanyId === companyIdToUse && campaignConfigs.length > 0) {
      return;
    }

    try {
      dispatch(setLoading(true));
      const categories = await getActiveCampaignConfigCategories();
      dispatch(setActiveCategories(categories));
      
      if (categories.length === 0) {
        dispatch(setConfigs([]));
      } else {
        const categoryNames = categories.map(category => category.name).join(',');
        const response = await getCampaignConfigs({
          companyId: companyIdToUse,
          statuses: 'active',
          sort: 'name_asc',
          limit: 100,
          offset: 0,
          applicationIds: applicationIdToUse,
          categories: categoryNames,
        });
        dispatch(setConfigs(response.items));
      }
      dispatch(setLastFetchedCompanyId(companyIdToUse));
    } catch (error) {
      console.error('Failed to load campaign configs:', error);
    } finally {
      dispatch(setLoading(false));
    }
  }, [
    dispatch,
    currentCompanyId,
    lastFetchedCompanyId,
    campaignConfigs.length,
    getActiveCampaignConfigCategories,
    getCampaignConfigs,
    applicationSettings?.applicationId,
  ]);

  return {
    loadCampaignConfigs,
  };
};
